import _, { find, get, includes, map, reduce, replace, round, startsWith } from 'lodash';
import {
  cancellationCouponSource,
  couponDiscountTypeMap,
  couponSourceTypeMap,
  couponTypes,
  marketingCampaignPageQueries,
} from '@/views/config/coupon/helper/constant';
import { getOnlinePlanName, getPlanSelectedNumbers } from '@/common/product';
import { BILLING_CYCLE } from '@/common/constants';
import { getNextPstCycleDate } from '@/utils/dateformat';
import { getDecimalsWithCurrency, NEED_REMOVE_DECIMAL_CURRENCY } from '@/utils/money';
import { isEmptyVal } from '@/utils';
import TermsConditionDialog from '@/views/config/coupon/dialog/terms-condition-dialog';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import { PLAN_PRICE_CHANGE_UTILS } from '@/common/priceChange';
import { getEffectiveCurrency } from '@/utils/currency';

export const getDefaultCouponState = () => {
  return {
    couponCode: null,
    couponType: couponTypes.NO_DISCOUNT,
    couponSourceType: null,
    timeBase: [],
    account_discount: 0,
    errorMessage: null,
    allowChange: true,
    discountInfo: {},
    discountInfoExtension: {},
    // because this field may come from verify coupon api, if the couponCategory is mt
    fromCampaignLink: false,
    // Just to distinguish which coupons are from campaign link
    fromPreloadLink: false,
    errorCode: null,
    tncMessage: null,
    subscriptionLevelAction: null,
  };
}

export const handleCouponErrMsg = (couponResult = {}) => {
  return {...getDefaultCouponState(), ...couponResult};
};

const getDefaultParams = () => {
  return {
    selectedPlanList: [],
    planWithAmountMap: {},
    discountPlanList: [],
  };
}

/**
 * coupon util function for threshold
 */
export class ThresholdCouponUtil {
  /**
   *
   * @param thresholdCoupon {ThresHoldCouponResponse}
   * @return {Array<number>}
   */
  getDiscountList = (thresholdCoupon = {}) => {
    const {thresholdList = []} = thresholdCoupon;
    return map(thresholdList, (item) => {
      return item?.thresholdValue;
    }).sort((a, b) => {
      return a - b;
    });
  }
  /**
   *
   * @param thresholdCoupon {ThresHoldCouponResponse}
   * @param getters
   * @return {{total: number, discountPlanMap: {}}}
   */
  getTotalAmount = (thresholdCoupon = {}, getters) => {
    const supportPlanCodeList = thresholdCoupon.supportPlanCodeList;
    const getSelectedPlansMap = getters.getEffectSelectedPlansMap;

    return reduce(Object.keys(getSelectedPlansMap), (res, planCode) => {
      const curSelectedPlan = get(getSelectedPlansMap, planCode);
      const realPlanCode = get(curSelectedPlan, "plan_code", "");
      if (includes(supportPlanCodeList, realPlanCode)) {
        const plan = getSelectedPlansMap[planCode];
        const singleTotal = (plan.amount / 100) * plan.cartInfo.selected_number;
        res.total = res.total + singleTotal;
        res.discountPlanMap[planCode] = singleTotal;
      }

      return res;
    }, {
      total: 0,
      discountPlanMap: {},
    });
  }

  getThresholdItem(thresholdList = [], newDiscount = 0) {
    return find(thresholdList, (item) => item.thresholdValue === newDiscount) || {};
  }

  /**
   *
   * @param totalAmount {totalAmount: number}
   * @param thresholdCoupon {ThresHoldCouponResponse}
   * @return {{nextThresholdItem: ThresHoldBean, actualDiscountAmount: number, actualTierAmount: number}}
   */
  getDiscountAmount = (totalAmount = 0, thresholdCoupon = {}) => {
    let newDiscount = 0;
    let newCurTierAmount = 0;
    const {thresholdList = []} = thresholdCoupon;
    const discountList = this.getDiscountList(thresholdCoupon);

    discountList.forEach((discount) => {
      if (totalAmount >= discount) {
        newDiscount = discount;
      }
    });

    if (newDiscount) {
      const thresholdItem = this.getThresholdItem(thresholdList, newDiscount);
      newDiscount = thresholdItem?.discountValue || 0;
      newCurTierAmount = thresholdItem?.thresholdValue || 0;
    }

    const index = discountList?.indexOf(newCurTierAmount);

    return {
      // The value of the total product amount closest to the minimum discount
      nextThresholdItem: this.getThresholdItem(thresholdList, get(discountList, `[${index > -1 ? index + 1 : 0}]`, null)),
      actualDiscountAmount: newDiscount,
      actualTierAmount: newCurTierAmount,
    };
  }
  /**
   *
   * @param thresholdCoupon {ThresHoldCouponResponse}
   * @param getters
   * @return { ThresHoldResponse }
   */
  calculateDisCount = (thresholdCoupon = {}, getters) => {
    const {total = 0, discountPlanMap} = this.getTotalAmount(thresholdCoupon, getters);
    const {nextThresholdItem, actualDiscountAmount, actualTierAmount} = this.getDiscountAmount(total, thresholdCoupon);
    const getSelectedPlansMap = getters.getEffectSelectedPlansMap;
    const thresholdDiscountPlans = reduce(Object.keys(getSelectedPlansMap), (res, planCode) => {
      const curSelectedPlan = get(getSelectedPlansMap, planCode);
      const realPlanCode = get(curSelectedPlan, "plan_code", "");

      if (includes(thresholdCoupon?.supportPlanCodeList, realPlanCode)) {
        const singleAmount = discountPlanMap[planCode];
        const singlePercentage = round(singleAmount / total, 10);
        const discountAmount = singlePercentage * actualDiscountAmount;
        res[planCode] = {
          ...getSelectedPlansMap[planCode],
          discountAmount,
        };
      }

      return res;
    }, {});

    return {
      thresholdDiscountPlans,
      actualDiscountAmount,
      actualTierAmount,
      totalAmount: total,
      nextTierAmount: nextThresholdItem?.thresholdValue || 0,
      nextDiscountAmount: nextThresholdItem?.discountValue || 0,
      gapTotalAmount: (nextThresholdItem?.thresholdValue || 0) - total,
    }
  }
  replaceTncInfo = (tnc = "", params = {}) => {
    const {nextInvoiceDate, amountWithoutDiscount, productNameList, totalDiscount} = params;

    if (tnc && totalDiscount) {
      tnc = replace(tnc, /\${next_invoice_date}/g, nextInvoiceDate);
      tnc = replace(tnc, /\${product_name_list}/g, productNameList);
      return replace(tnc, /\${amount_without_discount}/g, amountWithoutDiscount);
    }

    return "";
  }
}

export const thresholdCouponUtil = new ThresholdCouponUtil();

/**
 * @description Calculate the discount amount for each product
 * @param discountType
 * @param discount
 * @param amount
 * @param quantity
 * @return {number}
 */
export const calculateDiscountWithDiscountType = ({
  discountType = couponDiscountTypeMap.percentage,
  discount = 0,
  amount = 0,
  quantity = 1,
}) => {
  switch (discountType) {
    case couponDiscountTypeMap.fixedAmount:
      return Number(discount) * Number(quantity);
    case couponDiscountTypeMap.percentage:
    default:
      return (discount / 100) * amount;
  }
}

const calculatePerDiscount = (options = {}, selectedPlanList = [], planWithAmountMap = {}) => {
  const {planCode, discountType, discount } = options;

  const plan = find(selectedPlanList, p => p.plan_code === planCode) || {};
  const discountAmount = calculateDiscountWithDiscountType({
    discountType,
    discount,
    quantity: getPlanSelectedNumbers(plan),
    amount: get(planWithAmountMap, [planCode], 0),
  });

  const roundDiscountAmount = _.round(discountAmount / 100, 2) * 100;

  return {
    plan,
    discountAmount: roundDiscountAmount,
  }
}

/**
 * @description calculate marketing coupon or timebase coupon discount result
 * @param params {ProcessCouponParamsBean}
 * @return {CouponDiscountMapResultBean}
 */
export const processCoupon = (params = {}) => {
  const {discountPlanList, selectedPlanList, planWithAmountMap } = {...getDefaultParams(), ...params};

  // Calculate discount information for products contained in the shopping cart
  return _.reduce(discountPlanList, (result, item) => {
    // rest -> discount, expireDate, discountType, couponDefType, couponPeriod
    const {planCode, ...rest } = item;

    // with selected plan in shopping cart
    if (planWithAmountMap[planCode] && rest?.discount) {
      const {plan, discountAmount} = calculatePerDiscount(item, selectedPlanList, planWithAmountMap);

      if (plan.billing_cycle === BILLING_CYCLE.ANNUAL) {
        result.annualDiscount += discountAmount;
      } else if (plan.billing_cycle === BILLING_CYCLE.MONTHLY && !plan.isOneMonth) {
        result.monthlyDiscount += discountAmount;
      }
      result.totalDiscount += discountAmount;
      result.discountPlans[planCode] = {
        ...rest,
        discountAmount,
      };
    }

    return result;
  }, {
    annualDiscount: 0,
    monthlyDiscount: 0,
    totalDiscount: 0,
    discountPlans: {},
    thresholdResult: {},
  });
}

/**
 *
 * @description calculate threshold coupon discount result
 * @param couponState
 * @param getters
 * @return {CouponDiscountMapResultBean}
 */
export const generateThresholdCoupon = (couponState, getters) => {
  const thresholdResult = thresholdCouponUtil.calculateDisCount(couponState, getters);
  const thresholdDiscountPlans = thresholdResult.thresholdDiscountPlans;
  const result = {
    annualDiscount: 0,
    monthlyDiscount: 0,
    totalDiscount: 0,
    discountPlans: {},
  }

  const {nextAnnual, nextMonthly} = getNextPstCycleDate();

  Object.keys(thresholdDiscountPlans).forEach((planCode) => {
    const plan = thresholdDiscountPlans[planCode];
    const realDiscountAmount = plan?.discountAmount * 100;

    if (!validRoundValueIsZero(realDiscountAmount)) {
      let expireDate = "";
      if (plan.billing_cycle === BILLING_CYCLE.ANNUAL) {
        result.annualDiscount += realDiscountAmount;
        expireDate = nextAnnual;
      } else if (plan.billing_cycle === BILLING_CYCLE.MONTHLY) {
        result.monthlyDiscount += realDiscountAmount;
        expireDate = nextMonthly;
      }
      result.totalDiscount += realDiscountAmount;
      result.discountPlans[planCode] = {
        expireDate,
        discountAmount: realDiscountAmount,
        couponPeriod: 1,
        couponDefType: couponSourceTypeMap.threshold,
        discountType: couponDiscountTypeMap.thresholdAmount,
      };
    }
  });

  return {
    ...result,
    thresholdResult,
  }
}

// Whether the rounded value is equal to 0
export const validRoundValueIsZero = (amount, options = {
  divisionNumber: 100,
}) => {
  if (isNaN(+amount)) return false;
  const decimals = getDecimalsWithCurrency(amount);
  return round(amount / options.divisionNumber, decimals) === 0;
}

export const getPlanNamesWithBillingCycle = (thresholdDiscountPlans = {}) => {
  return reduce(Object.keys(thresholdDiscountPlans), (res, planCode) => {
    const plan = get(thresholdDiscountPlans, [planCode], {});
    const isOneMonth = get(plan, "isOneMonth", false);
    const {billing_cycle: billingCycle} = plan;
    let categoryName = getOnlinePlanName(plan);
    categoryName = categoryName ? `<p class="threshold-coupon-product-name">${categoryName}</p>` : "";

    if ((billingCycle === BILLING_CYCLE.MONTHLY || billingCycle === BILLING_CYCLE.PAY_AS_YOU_GO) && !isOneMonth) {
      res.monthly.push(categoryName);
    } else if (billingCycle === BILLING_CYCLE.ANNUAL) {
      res.annual.push(categoryName);
    }

    return res;
  }, {
    monthly: [],
    annual: [],
  })
}

export const showCouponTermsMessage = async (tncMessage = "") => {
  if (tncMessage) {
    const dialog = new ServiceConfirm(TermsConditionDialog);
    return dialog.confirm({ termsCondition: tncMessage, })
      .catch(() => Promise.reject());
  }

  return Promise.resolve();
}

export const isShortUrlWithCouponCode = (query = {}) => {
  return startsWith(location.pathname, '/z_url/') && isEmptyVal(query?.coupon) === false;
};

export const isFromCancelCampaign = () => {
  return window?.$config("cp_source") === cancellationCouponSource;
}

export const addPricingMappingCodeField = (list = [], key = 'planCode') => {
  return _.reduce(list, (pre, item) => {
    const planCode = _.get(item, key);
    const priceMappingPlanCode = PLAN_PRICE_CHANGE_UTILS.getOtherPlanCodeForPriceChange(planCode) || "";

    return [...pre, {
      ...item,
      priceMappingPlanCode,
    }];
  }, []);
};

export const getValidAmountInMinorCurrency = (amount) => {
  return NEED_REMOVE_DECIMAL_CURRENCY.includes(getEffectiveCurrency()) ? Math.round(amount * 100) : amount;
}

export const getValidAmountForHpmCharge = (amount) => {
  return NEED_REMOVE_DECIMAL_CURRENCY.includes(getEffectiveCurrency()) ? (amount / 100).toFixed(2) : amount;
}

export const isFromCampaignLink = () => {
  return isEmptyVal(window.$config?.('marketingCoupon')) === false;
}

export const getPersistCouponQueryInfo = () => {
  const queryCouponCode = window.$config('coupon') || window.$config('persistCoupon');

  if (queryCouponCode) {
    return {
      persistCoupon: queryCouponCode,
    };
  }

  return {};
};

// contain cartId/marketing coupon/partner referral
export const isFromMarketingCampaign = () => {
  return marketingCampaignPageQueries.some((key) => window.$config(key));
};
